import React, { Dispatch, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  IconButton,
  LockIcon,
  PlusIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TextInput,
  Tooltip,
  TooltipInteractiveWrapper,
  TrashIcon,
} from '@razorpay/blade/components';
import { AddComponentModal } from 'components/SalaryStructures/creation/components';
import { EarningsComponentType } from 'components/SalaryComponents/types';
import { SalaryComponent } from 'components/SalaryComponents/types';
import { nonDeletablePredfinedComponentTypes, salaryStructureComponentType } from 'components/SalaryStructure/constants';
import { CustomSalaryComponent, CustomSalaryComponentsAction } from './types';
import { groupComponentsBySection, salaryComponentToCustomSalaryComponent } from './utils';
import { INTEGER_AMOUNT } from 'utils/regexes';
import { toIndianCurrencyString } from 'utils/Numbers';

type CustomSalaryComponentsProps = {
  selectedComponents: CustomSalaryComponent[];
  dispatch: Dispatch<CustomSalaryComponentsAction>;
  isSubmitting: boolean;
  allComponents: EarningsComponentType[];
  isFetchingComponents: boolean;
  refetchComponents?: () => void;
};

export const CustomSalaryComponents = ({
  selectedComponents,
  dispatch,
  isSubmitting,
  allComponents,
  isFetchingComponents,
  refetchComponents = () => {},
}: CustomSalaryComponentsProps) => {
  const [isAddComponentModalOpen, setIsAddComponentModalOpen] = useState(false);

  const alreadyAddedComponentIds = selectedComponents
    .filter((c) => c.componentId)
    .map((c) => c.componentId);

  const { fixedEarnings, variableEarnings, otherCtcComponents } = groupComponentsBySection(
    selectedComponents,
    allComponents,
  );

  const onAddComponents = (components: SalaryComponent[]) => {
    const customComponents = components.map(salaryComponentToCustomSalaryComponent);
    const libraryComponentIds = allComponents.map((c) => c.id);
    dispatch({
      type: 'COMPONENTS_SYNC_FROM_MODAL',
      payload: { components: customComponents, libraryComponentIds },
    });
    setIsAddComponentModalOpen(false);
  };

  const onDeleteComponent = (id: string) => {
    dispatch({ type: 'COMPONENT_DELETED', payload: { id } });
  };

  const onComponentAmountChange = (id: string, amount: string) => {
    dispatch({
      type: 'COMPONENT_AMOUNT_CHANGED',
      payload: { id, amount },
    });
  };

  return (
    <Box marginTop="spacing.5">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.5">
        <Heading weight="regular" color="surface.text.gray.normal">
          Custom Salary Structure
        </Heading>
        <Button
          variant="secondary"
          size="small"
          icon={PlusIcon}
          onClick={() => setIsAddComponentModalOpen(true)}
          isDisabled={isFetchingComponents || isSubmitting}
          isLoading={isFetchingComponents}>
          Add component
        </Button>
      </Box>
      <ComponentsTable
        heading="Fixed Earnings"
        components={fixedEarnings}
        isSubmitting={isSubmitting}
        onAmountChange={onComponentAmountChange}
        onDelete={onDeleteComponent}
        showHeaderRow
        showAmountColumnHeader
      />
      {variableEarnings.length ? (
        <ComponentsTable
          heading="Variable Earnings"
          components={variableEarnings}
          isSubmitting={isSubmitting}
          onAmountChange={onComponentAmountChange}
          onDelete={onDeleteComponent}
        />
      ) : null}
      {otherCtcComponents.length ? (
        <ComponentsTable
          heading="Other CTC Components"
          components={otherCtcComponents}
          isSubmitting={isSubmitting}
          onAmountChange={onComponentAmountChange}
          onDelete={onDeleteComponent}
        />
      ) : null}

      <AddComponentModal
        isOpen={isAddComponentModalOpen}
        onClose={() => setIsAddComponentModalOpen(false)}
        availableComponents={allComponents as SalaryComponent[]}
        selectedComponentIds={alreadyAddedComponentIds}
        onAddComponents={onAddComponents}
        refetchComponents={refetchComponents}
      />
    </Box>
  );
};

export const ComponentsTable = ({
  heading,
  components,
  isSubmitting,
  onAmountChange,
  onDelete,
  showHeaderRow = true,
  showAmountColumnHeader = false,
}: {
  heading: string;
  components: CustomSalaryComponent[];
  isSubmitting: boolean;
  onAmountChange: (id: string, amount: string) => void;
  onDelete: (id: string) => void;
  showHeaderRow?: boolean;
  showAmountColumnHeader?: boolean;
}) => {
  return (
    <Table data={{ nodes: components }} gridTemplateColumns="1fr 1fr 40px">
      {(tableData) => (
        <>
          {!!showHeaderRow && (
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text size="medium" variant="body">
                    {heading}
                  </Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text size="medium" variant="body">
                    {showAmountColumnHeader ? 'Yearly Amount' : ' '}
                  </Text>
                </TableHeaderCell>
                <TableHeaderCell> </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
          )}
          <TableBody>
            {tableData.map((component) => {
              const isComponentDeletable = !nonDeletablePredfinedComponentTypes.includes(
                component.componentType || 'COMPONENT_TYPE_NA',
              );
              const isSA = component.componentType === salaryStructureComponentType.COMPONENT_TYPE_SPECIAL_ALLOWANCE;
              const isResidualSA = isSA;

              return (
                <TableRow key={component.id} item={component}>
                  <TableCell>
                    <Box whiteSpace="normal">
                      <Text
                        size="medium"
                        variant="body"
                        weight="regular"
                        truncateAfterLines={1}
                        wordBreak="break-all">
                        {component.name}
                      </Text>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box width="100%">
                      {isResidualSA ? (
                        <Box>
                        <Text
                          size="medium"
                          variant="body"
                          weight="regular"
                          truncateAfterLines={1}
                          wordBreak="break-all">
                          Residual component
                        </Text>
                        <Text  size="medium"
                          variant="body"
                          color='surface.text.gray.muted'
                          weight="regular"
                          truncateAfterLines={1}
                          wordBreak="break-all">CTC - Sum of all other components</Text>
                        </Box>
                      ) : (
                        <TextInput
                          placeholder="Enter Amount"
                          accessibilityLabel={`${component.name || 'Component'} amount`}
                          value={toIndianCurrencyString(
                            component.amount ? Number(component.amount) : 0,
                            true,
                          )}
                          isDisabled={isSubmitting}
                          onChange={({ value }) => {
                            if (INTEGER_AMOUNT.test(value || '')) {
                              const numValue = Number(value?.replace(/,/g, '')) ?? 0;
                              onAmountChange(component.id, numValue.toString());
                            }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isComponentDeletable ? (
                      <IconButton
                        isDisabled={isSubmitting}
                        onClick={() => onDelete(component.id)}
                        icon={TrashIcon}
                        emphasis="subtle"
                        size="medium"
                        accessibilityLabel="delete component"
                      />
                    ) : (
                      <Tooltip content="Component is required in the salary structure as per government compliance">
                        <TooltipInteractiveWrapper display="grid" placeItems="center" width="100%">
                          <LockIcon color="surface.icon.gray.disabled" size="medium" />
                        </TooltipInteractiveWrapper>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </>
      )}
    </Table>
  );
};